import React, { createContext, ReactNode, useEffect, useMemo } from "react";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { AnalyticsEvent } from "@/hooks/useAnalytics";

interface AnalyticsContextValue {
  track: (event: AnalyticsEvent, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  reset: () => void;
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(
  null,
);

interface AnalyticsProviderProps {
  children: ReactNode;
}

// Create a single AnalyticsBrowser instance.
// Methods called before .load() are automatically buffered and flushed after load.
const analytics = new AnalyticsBrowser();
let isLoaded = false;

/** @internal Exported for testing only */
export function __unsafe_resetForTests() {
  isLoaded = false;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  useEffect(() => {
    if (isLoaded) return;

    const writeKey = window.ENV?.SEGMENT_KEY;

    if (!writeKey) {
      console.warn("Segment write key not found, analytics disabled");
      return;
    }

    // Load triggers destination loading and flushes any buffered events
    analytics.load({ writeKey });
    isLoaded = true;
  }, []);

  const value = useMemo(
    () => ({
      // Async because analytics.user() returns a Promise on AnalyticsBrowser.
      // This allows us to include cached user traits in every track event.
      track: async (
        event: AnalyticsEvent,
        properties?: Record<string, unknown>,
      ) => {
        try {
          const user = await analytics.user();
          analytics.track(event, properties, {
            context: { traits: user.traits() },
          });
        } catch {
          // Fall back to tracking without traits
          analytics.track(event, properties);
        }
      },
      identify: (userId: string, traits?: Record<string, unknown>) => {
        analytics.identify(userId, traits);
      },
      reset: () => {
        analytics.reset();
      },
    }),
    [],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
